/**
 * Visitor attribution helpers.
 *
 * - getVisitorId(): stable anonymous id stored in localStorage (30-day rotate)
 * - captureUtm(): reads UTM params from current URL and persists them with TTL
 * - getAttributionPayload(): returns {visitor_id, utm_*, referrer, landing_page}
 *   for sending with contact submissions and chat messages.
 */

const VID_KEY = "sm_visitor_id";
const UTM_KEY = "sm_attribution";
const UTM_TTL_DAYS = 30;

function _now() { return Date.now(); }

function _rand() {
  return (
    Math.random().toString(36).slice(2) +
    _now().toString(36)
  );
}

export function getVisitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = "v_" + _rand();
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return "";
  }
}

export function captureUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm_source = params.get("utm_source");
    const utm_medium = params.get("utm_medium");
    const utm_campaign = params.get("utm_campaign");
    const landing_page = window.location.pathname;
    const referrer = document.referrer || "";

    const existing = _readAttribution();
    if (existing && !utm_source && !utm_medium && !utm_campaign) {
      return existing;
    }
    const attr = {
      visitor_id: getVisitorId(),
      utm_source: utm_source || existing?.utm_source || null,
      utm_medium: utm_medium || existing?.utm_medium || null,
      utm_campaign: utm_campaign || existing?.utm_campaign || null,
      referrer: existing?.referrer || referrer || null,
      landing_page: existing?.landing_page || landing_page,
      saved_at: _now(),
    };
    localStorage.setItem(UTM_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return null;
  }
}

function _readAttribution() {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw);
    if (a?.saved_at && _now() - a.saved_at > UTM_TTL_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(UTM_KEY);
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

import { canTrackAnalytics } from "@/lib/consent";

export function getAttributionPayload() {
  // GDPR: if user hasn't consented to analytical cookies, return a payload
  // with no identifiers / UTM so the contact form still works without tracking.
  if (!canTrackAnalytics()) {
    return {
      visitor_id: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      referrer: null,
      landing_page: window.location.pathname,
    };
  }
  const attr = _readAttribution() || captureUtm() || {};
  return {
    visitor_id: attr.visitor_id || getVisitorId(),
    utm_source: attr.utm_source || null,
    utm_medium: attr.utm_medium || null,
    utm_campaign: attr.utm_campaign || null,
    referrer: attr.referrer || null,
    landing_page: attr.landing_page || window.location.pathname,
  };
}
